import { gql } from "apollo-boost";
import { UserClient } from "./apolloClient";

// The ownership of RV orders is kept by the user backend (UserClient), apart
// from the orders themselves, which the ConnX backend holds.

// Make a user the owner of an order. Refused when it already has one. Allowed
// to the user themselves, or a holder of 'rv_owner_assign'.
export const ASSIGN_OWNER = gql`
  mutation($userID: ID!, $orderID: ID!) {
    rvOrderOwner(userID: $userID, orderID: $orderID)
  }
`;

// Remove the given owner from an order. Refused when they are not its owner.
// Allowed to the owner themselves, or a holder of 'rv_owner_revoke'.
export const REVOKE_OWNER = gql`
  mutation revokeRVOrderOwner($userID: ID!, $orderID: ID!) {
    revokeRVOrderOwner(userID: $userID, orderID: $orderID) {
      id
    }
  }
`;

export interface RevokeOwnerResult {
  revokeRVOrderOwner: {
    id: string;
  };
}

// The owner of an order, or null when it has none. Requires 'rv_owner_list'.
export const ORDER_OWNER = gql`
  query rvOrderOwner($orderID: ID!) {
    rvOrderOwner(orderID: $orderID) {
      id
      email
    }
  }
`;

export interface OrderOwner {
  id: string;
  email: string;
}

export interface OrderOwnerResult {
  rvOrderOwner: OrderOwner | null;
}

// The owners of the given orders, one entry per order that has one. Requires
// 'rv_owner_list'. At most 100 order IDs.
export const ORDER_OWNERS = gql`
  query rvOrderOwners($orderIDs: [ID!]!) {
    rvOrderOwners(orderIDs: $orderIDs) {
      orderID
      owner {
        id
        email
      }
    }
  }
`;

export interface OrderOwnersResult {
  rvOrderOwners: { orderID: string; owner: OrderOwner }[];
}

// getRVOrderOwners gives the owners of the given orders by order ID; an order
// missing from the result has none.
export const getRVOrderOwners = async (
  orderIDs: string[]
): Promise<Record<string, OrderOwner>> => {
  if (orderIDs.length === 0) return {};
  const r = await UserClient.query<OrderOwnersResult>({
    query: ORDER_OWNERS,
    variables: { orderIDs },
  });
  return r.data.rvOrderOwners.reduce<Record<string, OrderOwner>>(
    (acc, { orderID, owner }) => ({ ...acc, [orderID]: owner }),
    {}
  );
};
